import { Tab, Tooltip } from "@blueprintjs/core"
import { dateHelpers } from "@runn/calculations"
import cc from "classcat"
import React, { useRef, useState } from "react"
import { useEffect } from "react"
import { flushSync } from "react-dom"
import { graphql, useFragment } from "react-relay"

import styles from "./ProjectBudgetForm.module.css"

import { ProjectBudgetForm_project$key } from "~/forms/ProjectBudgetForm/__generated__/ProjectBudgetForm_project.graphql"
import { ProjectBudgetForm_user$key } from "~/forms/ProjectBudgetForm/__generated__/ProjectBudgetForm_user.graphql"

import currency, { formatCurrency } from "~/helpers/CurrencyHelper"
import { track } from "~/helpers/analytics"
import {
  OTHER_EXPENSES_ROLE,
  OtherCostWithCalculations,
  addDerivedFields,
  extractRoles,
  getAllocationMessage,
  getTotalExpenses,
} from "~/helpers/otherCosts"
import { FinancialType } from "~/helpers/permissions"
import { isBillableProject } from "~/helpers/project-helpers"
import { getDefaultRate } from "~/helpers/rate-helpers"

import ProjectClientIcon from "~/common/ProjectClientIcon"
import Tabs from "~/common/Tabs"
import Button from "~/common/buttons/Button"

import { projectBudgetUpdateV2Relay } from "~/mutations/ProjectBudget"

import {
  PRICING_MODELS,
  PricingModelSelection,
  RATE_TYPES,
  RateTypeSelection,
} from "~/GLOBALVARS"
import { Card } from "~/dashboard/Card"
import PhaseEditor from "~/forms/ProjectBudgetForm/Phase/PhaseEditor"
import ProjectRatesEditor from "~/forms/ProjectBudgetForm/ProjectRates/ProjectRatesEditor"
import { BudgetedRole, ProjectRate } from "~/forms/ProjectBudgetForm/types"
import { getSettings, setSetting } from "~/localsettings"

import BudgetEditor from "./Budget/BudgetEditor"
import BudgetSummary from "./Budget/BudgetSummary"
import {
  getBudgetedRoles,
  getProjectTotalAllocation,
} from "./Budget/project_budget_helpers"
import NonBillableWarning from "./NonBillableWarning"
import OtherExpensesEditor, {
  createEmptyExpense,
} from "./OtherExpenses/OtherExpensesEditor"

type Props = {
  project: ProjectBudgetForm_project$key
  user: ProjectBudgetForm_user$key
  onClose: () => void
}

type BudgetTabID = "expenses" | "project-rates" | "budget"

export type RateCardOption = { value: number; label: string }

const userFragment = graphql`
  fragment ProjectBudgetForm_user on users {
    id
    permissions
    account {
      id
      default_full_time_minutes
      roles {
        id
        name
        active
      }
      rate_cards {
        id
        name
        blended_rate: blended_rate_private
        blended_rate_card
        role_charge_out_rates {
          role_id
          rate_card_id
          charge_out_rate: charge_out_rate_private
        }
      }
    }
  }
`

const projectFragment = graphql`
  fragment ProjectBudgetForm_project on projects {
    id
    name
    active
    pricing_model
    rate_type
    total_budget: total_budget_private
    no_phase_revenue_budget: no_phase_revenue_budget_private
    expenses_budget: expenses_budget_private
    emoji
    is_template
    budget_method
    project_rates {
      id
      projectId: project_id
      roleId: role_id
      rate: rate_private
    }
    rate_card {
      id
      name
      blended_rate: blended_rate_private
      blended_rate_card
      role_charge_out_rates {
        role_id
        rate_card_id
        charge_out_rate: charge_out_rate_private
      }
    }
    project_roles {
      id
      role_id
      estimated_minutes
    }
    members {
      id
      role_id
    }
    client {
      internal
      id
      image_key
      website
    }
    other_costs(order_by: { date: asc }) {
      id
      name
      date: date_runn
      cost
      charge
      phaseId: phase_id
    }
    phases {
      id
      revenue_budget: revenue_budget_private
    }

    ...PhaseEditor_project
    ...OtherExpensesEditor_project
  }
`

const ProjectBudgetForm = (props: Props) => {
  const { onClose } = props
  const viewer = useFragment<ProjectBudgetForm_user$key>(
    userFragment,
    props.user,
  )
  const project = useFragment<ProjectBudgetForm_project$key>(
    projectFragment,
    props.project,
  )
  const {
    account,
    account: { default_full_time_minutes },
  } = viewer

  const [isSaving, setIsSaving] = useState(false)

  const isBillable = isBillableProject(
    project.pricing_model,
    project.client.internal,
  )

  const initialVisibleRoles = getBudgetedRoles(
    project,
    account.roles,
    isBillable,
  )

  const [roles, setRoles] =
    useState<ReadonlyArray<BudgetedRole>>(initialVisibleRoles)

  const [deletedRoles, setDeletedRoles] = useState<number[]>([])

  const [projectRates, setProjectRates] = useState<ProjectRate[]>([
    ...project.project_rates,
  ])

  const financialPermission = viewer.permissions.financial as FinancialType
  const hideFinancials = financialPermission === FinancialType.None

  const [showNonBillableView, setShowNonBillableView] = useState(!isBillable)

  const [billingMethod, setBillingMethod] = useState<PricingModelSelection>(
    PRICING_MODELS.find((pm) => pm.value === project.pricing_model),
  )
  const updatingToNonBillable = isBillable && billingMethod.value === "nb"

  const rateCards: RateCardOption[] = account.rate_cards.map((rateCard) => ({
    value: rateCard.id,
    label: rateCard.name,
  }))

  const [selectedRateCard, setSelectedRateCard] = useState<RateCardOption>({
    value: project.rate_card.id,
    label: project.rate_card.name,
  })

  const [selectedRateType, setSelectedRateType] = useState<RateTypeSelection>(
    RATE_TYPES.find((rt) => rt.value === project.rate_type) || RATE_TYPES[0],
  )

  let defaultTabId = getSettings().projectBudgetTabId ?? "budget"
  // Edge case where an unpermitted tab was persisted
  if (defaultTabId === "expenses" && hideFinancials) {
    defaultTabId = "budget"
  }

  const [budgetTabId, setBudgetTabId] = useState<BudgetTabID>(defaultTabId)

  useEffect(() => {
    track("Tab Changed", { type: "Project Budget", tab: budgetTabId })
    if (budgetTabId === "expenses" || budgetTabId === "budget") {
      setSetting("projectBudgetTabId", budgetTabId)
    }
  }, [budgetTabId])

  const projectOtherExpenses = (project.other_costs ?? []).map((expense) => {
    return {
      ...expense,
      date: dateHelpers.parseRunnDate(expense.date),
    }
  })
  const initialExpenses = projectOtherExpenses.length
    ? projectOtherExpenses.map(addDerivedFields)
    : [createEmptyExpense()]

  const [otherExpenses, setOtherExpenses] =
    useState<Array<OtherCostWithCalculations>>(initialExpenses)

  const [phaseBudgets, setPhaseBudgets] = useState(
    project.phases.map((phase) => ({
      phaseId: phase.id,
      revenueBudget: phase.revenue_budget,
    })),
  )

  const handlePhaseUpdate = (phaseId: number, revenueBudget: number) => {
    setPhaseBudgets((prevBudgets) => {
      const existingIndex = prevBudgets.findIndex((b) => b.phaseId === phaseId)
      if (existingIndex !== -1) {
        const newBudgets = [...prevBudgets]
        newBudgets[existingIndex] = { phaseId: phaseId, revenueBudget }
        return newBudgets
      }
      return [...prevBudgets, { phaseId: phaseId, revenueBudget }]
    })
  }

  const [noPhaseBudget, setNoPhaseBudget] = useState<number>(
    project.no_phase_revenue_budget,
  )

  const totalAllocated = getProjectTotalAllocation(roles, projectRates)
  const totalExpenses = getTotalExpenses(otherExpenses)
  const [totalBudget, setTotalBudget] = useState(project.total_budget)
  const [budgetMethod, setBudgetMethod] = useState(project.budget_method)
  const [itemNameRequiredError, setItemNameRequiredError] = useState(false)

  useEffect(() => {
    const expenseRole = extractRoles(roles).expenses
    // if you've added expenses and "other expenses" is not in your budget, add it implicitly.
    if (totalExpenses.charge && !expenseRole && !showNonBillableView) {
      setRoles([
        ...roles,
        {
          ...OTHER_EXPENSES_ROLE,
          estimatedMinutes: 0,
          projectRoleId: OTHER_EXPENSES_ROLE.id,
          implicit: true,
          isScheduled: true,
        },
      ])
    }
    // If you have no expenses and "other expenses" is sitting empty in your budget, assume it was added implicitly and clean it up.
    else if (
      !totalExpenses.charge &&
      expenseRole &&
      expenseRole.implicit &&
      !expenseRole.estimatedMinutes
    ) {
      setRoles(roles.filter((r) => r.id !== OTHER_EXPENSES_ROLE.id))
    }
    // If you've switched to NB, remove Other Expenses from the list of roles. It doesn't apply.
    else if (showNonBillableView && expenseRole) {
      setRoles(roles.filter((r) => r.id !== OTHER_EXPENSES_ROLE.id))
    }
    // Ensure the isScheduled property of the expense role is set correctly. The role can be added before or after the existence of expenses, so these need to be in sync.
    else if (expenseRole) {
      const index = roles.findIndex((r) => r.id === OTHER_EXPENSES_ROLE.id)
      if (index !== -1) {
        const shouldBeScheduled = totalExpenses.charge > 0
        if (expenseRole.isScheduled !== shouldBeScheduled) {
          const newRoles = [...roles]
          newRoles[index].isScheduled = shouldBeScheduled
          setRoles(newRoles)
        }
      }
    }
  }, [totalExpenses.charge, roles, showNonBillableView])

  const unallocatedRoles = account.roles
    .filter((r) => r.active && !roles.map((x) => x.id).includes(r.id))
    .map((r) => {
      const existingBudgetedRole = project.project_roles.find(
        (pr) => r.id === pr.role_id,
      )?.id

      return {
        ...r,
        estimatedMinutes: 0,
        projectRoleId: existingBudgetedRole,
      }
    })

  const allocatedRoleData = extractRoles(roles)

  const isCompleteExpense = (expense: OtherCostWithCalculations) =>
    expense.name.trim() || expense.charge !== 0 || expense.cost !== 0

  if (
    !allocatedRoleData.expenses &&
    !otherExpenses.filter(isCompleteExpense).length &&
    !showNonBillableView
  ) {
    unallocatedRoles.push({
      ...OTHER_EXPENSES_ROLE,
      estimatedMinutes: 0,
      projectRoleId: OTHER_EXPENSES_ROLE.id,
    })
  }
  const expensesBudget = allocatedRoleData.expenses?.estimatedMinutes ?? null

  const expensesAllocationDifference = expensesBudget - totalExpenses.charge
  const budgetAllocationDifference =
    budgetMethod === "PHASE"
      ? totalBudget -
        (phaseBudgets?.reduce((sum, phase) => sum + phase.revenueBudget, 0) +
          noPhaseBudget)
      : totalBudget - totalAllocated

  const isExpensesTab = budgetTabId === "expenses"
  const allocationDifference = isExpensesTab
    ? expensesAllocationDifference
    : budgetAllocationDifference

  // Determines whether to show the budget difference in the footer
  // For expenses tab: Show if there's an expense budget AND actual expenses exist AND budget method is ROLE
  // For budget tab: Show if there's any total budget set
  const showDifference = isExpensesTab
    ? !!expensesBudget && totalExpenses.charge > 0 && budgetMethod === "ROLE"
    : budgetTabId === "budget" && !!totalBudget

  const overAllocated = allocationDifference < 0

  const rateCard = account.rate_cards.find(
    (rc) => rc.id === selectedRateCard.value,
  )

  const handleBillingMethod = (selection: PricingModelSelection): void => {
    setBillingMethod(selection)
    setShowNonBillableView(selection.value === "nb")

    if (selection.value === "nb") {
      setBudgetMethod("ROLE")
    }
  }

  const handleShowAllRoles = () => {
    setRoles([...roles, ...unallocatedRoles])
  }

  const handleAddRole = (roleId: number) => {
    const selectedRole = unallocatedRoles.find((r) => r.id === roleId)
    setRoles([...roles, selectedRole])
  }

  const handleItemNameRequired = (expenses: OtherCostWithCalculations[]) => {
    const hasError = expenses.some(
      (expense) =>
        // Error if: no name BUT has a cost or charge
        (!expense.name || expense.name.trim() === "") &&
        (expense.charge !== 0 || expense.cost !== 0),
    )

    setItemNameRequiredError(hasError)
  }

  const handleExpenseUpdate = (
    id: number,
    expense: OtherCostWithCalculations,
  ) => {
    flushSync(() => {
      const updatedExpenses = [...otherExpenses]
      const index = updatedExpenses.findIndex((c) => c.id === id)
      if (index !== -1) {
        updatedExpenses[index] = { ...expense }
        handleItemNameRequired(updatedExpenses)
        setOtherExpenses(updatedExpenses)
      }
    })
  }

  const handleExpenseDelete = (id: number) => {
    const expense = otherExpenses.find((c) => c.id === id)
    if (!expense) {
      return
    }
    track("Other Cost Deleted", {
      cost: expense.cost,
      charge: expense.charge,
    })
    const updatedExpenses = otherExpenses.filter((c) => c.id !== id)
    if (!updatedExpenses.length) {
      updatedExpenses.push(createEmptyExpense())
    }
    setOtherExpenses(updatedExpenses)
    handleItemNameRequired(updatedExpenses)
  }

  const handleExpenseAdd = () => {
    track("Other Cost Added")
    const expenses = [...otherExpenses, createEmptyExpense()]
    handleItemNameRequired(expenses)
    setOtherExpenses(expenses)
  }

  const handleBudgetUpdateRole = (updatedRole: BudgetedRole) => {
    flushSync(() => {
      const index = roles.findIndex((r) => r.id === updatedRole.id)
      if (index !== -1) {
        const newRoles = [...roles]
        newRoles[index] = updatedRole
        setRoles(newRoles)
      }
    })
  }

  const handleDeleteRole = (roleId: number) => {
    setRoles([...roles.filter((r) => r.id !== roleId)])
    setDeletedRoles([...deletedRoles, roleId])
  }

  const handleBudgetUpdateRate = (updatedRates: ProjectRate[]) => {
    setProjectRates((prevRates) => {
      const newRates = [...prevRates]

      updatedRates.forEach((updatedRate) => {
        const index = newRates.findIndex((r) => r.roleId === updatedRate.roleId)
        if (index === -1) {
          newRates.push(updatedRate)
        } else {
          newRates[index] = updatedRate
        }
      })

      return newRates
    })
  }

  const saveBudget = async () => {
    setIsSaving(true)

    roles.forEach((r) => {
      delete r.isScheduled
    })

    const unallocated_project_role_ids = deletedRoles
      .map(
        (role_id) =>
          project.project_roles.find((pr) => pr.role_id === role_id)?.id,
      )
      .filter((id) => id)
    const unallocated_project_rate_ids = deletedRoles
      .map(
        (role_id) =>
          project.project_rates.find((pr) => pr.roleId === role_id)?.id,
      )
      .filter((id) => id)

    const rates = projectRates.map((r) => {
      let rate = r.rate
      const roleExists = !!roles.find((role) => role.id === r.roleId)
      if (showNonBillableView) {
        rate = 0
      } else if (!roleExists) {
        // Reset the rate to the default rate if the role has been removed
        rate = getDefaultRate(rateCard, r.roleId)
      }
      return {
        ...r,
        projectId: project.id,
        roleId: r.roleId,
        rate,
      }
    })

    const baseUpdateInput = {
      id: project.id,
      unallocatedProjectRoleIds: unallocated_project_role_ids,
      unallocatedProjectRateIds: unallocated_project_rate_ids,
      roles: allocatedRoleData.people,
    }

    const formatOtherExpense = (expense) => {
      // TODO = I think we can get rid of this silly `--new` stuff once we remove
      // the existing modal. This conditional bit here is simpler for the moment.
      // Shared types between this new project budget and the old one make refactoring
      // harder until the old one stops referring to these types.
      const id = String(expense.id).startsWith("new--")
        ? undefined
        : (expense.id as number)

      return {
        name: expense.name,
        charge: expense.charge,
        cost: expense.cost,
        date: dateHelpers.formatToRunnDate(expense.date),
        id,
        phaseId: expense.phaseId,
      }
    }

    const financialUpdateInput = {
      ...baseUpdateInput,
      budgetMethod,
      rates,
      pricingModel: billingMethod.value,
      totalBudget: typeof totalBudget === "number" ? Number(totalBudget) : null,
      expensesBudget,
      rateType: selectedRateType.value,
      rateCardId: selectedRateCard.value,
      otherExpenses: otherExpenses
        .filter((expense) => expense.name.trim() !== "")
        .map(formatOtherExpense),
      ...(budgetMethod === "PHASE" && {
        phaseBudgets: phaseBudgets.length > 0 ? phaseBudgets : undefined,
        noPhaseBudget,
      }),
      ...(budgetMethod === "ROLE" && {
        rates,
      }),
    }

    await projectBudgetUpdateV2Relay({
      input: hideFinancials ? baseUpdateInput : financialUpdateInput,
      todaysDate: dateHelpers.getTodaysDate(),
    })

    track("Project Budget Updated", {
      pricing_model: billingMethod.value,
      rate_type: selectedRateType.value,
      role_count: roles.length,
      other_cost_count: otherExpenses.length,
    })
    setIsSaving(false)
    onClose()
  }

  const tableFooterRef = useRef(null)
  const footerButtonsRef = useRef(null)

  const footerHeight =
    tableFooterRef.current?.clientHeight +
    footerButtonsRef.current?.clientHeight

  const invalidBudget = Number(totalBudget) < 0

  const handleExpensesBudgetUpdate = (val: number) => {
    const { expenses } = extractRoles(roles)
    if (expenses) {
      const updatedExpenses = {
        ...expenses,
        estimatedMinutes: val,
      }
      handleBudgetUpdateRole(updatedExpenses)
    } else {
      setRoles([
        ...roles,
        {
          ...OTHER_EXPENSES_ROLE,
          estimatedMinutes: val,
          projectRoleId: OTHER_EXPENSES_ROLE.id,
          implicit: true,
          isScheduled: true,
        },
      ])
    }
  }

  const Summary = (
    <>
      <BudgetSummary
        billingMethod={billingMethod}
        totalBudget={totalBudget}
        invalid={invalidBudget}
        currency={currency}
        showNonBillableView={showNonBillableView}
        allowBillingMethodEdit={!project.client.internal}
        budgetMethod={budgetMethod}
        onChangeBillingMethod={handleBillingMethod}
        onChangeTotalBudget={setTotalBudget}
        onChangeBudgetMethod={setBudgetMethod}
      />
      {updatingToNonBillable && <NonBillableWarning />}
    </>
  )

  const BudgetPanel = (
    <>
      {!hideFinancials && Summary}
      {budgetMethod === "PHASE" ? (
        <PhaseEditor
          project={project}
          phaseBudgets={phaseBudgets}
          noPhaseBudget={noPhaseBudget}
          canViewFinancials={!hideFinancials}
          onPhaseUpdate={handlePhaseUpdate}
          onNoPhaseUpdate={setNoPhaseBudget}
        />
      ) : (
        <BudgetEditor
          rateType={selectedRateType}
          showNonBillableView={showNonBillableView}
          roles={roles}
          hideFinancials={hideFinancials}
          rateCard={rateCard}
          projectRates={projectRates}
          projectId={project.id}
          defaultFullTimeMinutes={default_full_time_minutes}
          expensesAllocationDifference={expensesAllocationDifference}
          expensesBudget={expensesBudget}
          budgetMethod={budgetMethod}
          totalAllocated={totalAllocated}
          unallocatedRoles={unallocatedRoles}
          onUpdateRole={handleBudgetUpdateRole}
          onUpdateRates={handleBudgetUpdateRate}
          onDeleteRole={handleDeleteRole}
          onChangeRateType={setSelectedRateType}
          onAddRole={handleAddRole}
          onShowAllRoles={handleShowAllRoles}
        />
      )}
    </>
  )

  const ProjectRatesPanel = (
    <ProjectRatesEditor
      projectId={project.id}
      rateType={selectedRateType}
      showNonBillableView={showNonBillableView}
      roles={roles}
      hideFinancials={hideFinancials}
      rateCard={rateCard}
      rateCards={rateCards}
      selectedRateCard={selectedRateCard}
      projectRates={projectRates}
      defaultFullTimeMinutes={default_full_time_minutes}
      unallocatedRoles={unallocatedRoles}
      onAddRole={handleAddRole}
      onShowAllRoles={handleShowAllRoles}
      onUpdateRates={handleBudgetUpdateRate}
      onDeleteRole={handleDeleteRole}
      onChangeRateCard={setSelectedRateCard}
    />
  )

  const ExpensesPanel = (
    <OtherExpensesEditor
      project={project}
      showNonBillableView={showNonBillableView}
      expenses={otherExpenses}
      totalExpenses={totalExpenses}
      expensesBudget={expensesBudget || 0}
      budgetMethod={budgetMethod}
      onUpdate={handleExpenseUpdate}
      onDelete={handleExpenseDelete}
      onAdd={handleExpenseAdd}
      onExpensesBudgetUpdate={handleExpensesBudgetUpdate}
    />
  )

  return (
    <Card style={{ width: "900px" }}>
      <div
        className={`
          ${styles.budgetContainer}
          ${hideFinancials || showNonBillableView ? styles.hideFinancials : ""}
        `}
        style={{
          maxHeight: `calc(100% - ${footerHeight}px)`,
          minHeight: "200px",
        }}
        data-test="project-budget"
      >
        <div className={styles.client}>
          <ProjectClientIcon project={project} client={project.client} border />
          <span>{project.name}</span>
        </div>
        <Tabs
          defaultSelectedTabId={budgetTabId}
          large
          id="panelBudget"
          onChange={(tabId: BudgetTabID) => setBudgetTabId(tabId)}
          rightTabs
        >
          <Tab
            id="budget"
            title="Budget"
            panel={BudgetPanel}
            style={{ paddingBottom: "18px" }}
          />
          {!showNonBillableView && !hideFinancials && (
            <Tab
              id="project-rates"
              title="Project Rates"
              panel={ProjectRatesPanel}
              style={{ paddingBottom: "18px" }}
            />
          )}
          {!hideFinancials && (
            <Tab
              id="expenses"
              title="Other Expenses"
              panel={ExpensesPanel}
              style={{ paddingBottom: "18px" }}
            />
          )}
        </Tabs>
      </div>

      <div className={styles.footerButtons} ref={footerButtonsRef}>
        {!showNonBillableView && showDifference && (
          <div
            data-test="budget-difference"
            className={cc([
              styles.footerMessage,
              {
                [styles.over]: overAllocated,
              },
            ])}
          >
            {getAllocationMessage(allocationDifference)}
            {allocationDifference !== 0 && (
              <span>{formatCurrency(Math.abs(allocationDifference))}</span>
            )}
          </div>
        )}
        <Button
          text="Cancel"
          data-test="cancel-budget-button"
          onClick={onClose}
          style={{ marginLeft: "auto" }}
        />
        <Tooltip
          content="Other expense must have an item name"
          autoFocus={false}
          disabled={!itemNameRequiredError}
        >
          <Button
            data-test="save-budget-button"
            loading={isSaving}
            text="Save"
            outlined={false}
            onClick={saveBudget}
            disabled={
              isSaving ||
              (totalBudget && Number(totalBudget) < 0) ||
              itemNameRequiredError
            }
          />
        </Tooltip>
      </div>
    </Card>
  )
}

export default ProjectBudgetForm
